import React, {ReactNode} from 'react';
import {NavLink} from 'react-router-dom';
import {INavigationItem} from '@src/@interfaces/Navigation';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import {MenuProps, MenuState} from '@src/@types/components/Menu';
import {UserContext} from '@src/contexts/UserContext';
import getUri from '@src/helpers/getUri';

export default abstract class AbstractMenu<
        P extends MenuProps = MenuProps,
        S extends MenuState = MenuState
> extends React.PureComponent<P, S> {
    protected abstract subMenuComponent: ReactNode;

    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    render(): ReactNode {
        return <ul {...this.getULListProps()}>
            {this.renderItems()}
        </ul>;
    }

    protected getULListProps(): React.HTMLProps<HTMLUListElement & React.AriaAttributes> {
        return {className: this.getULListClassName()};
    }

    protected getULListClassName(): string | undefined {
        return this.props.menuClassName;
    }

    protected toggleItem(index: number) {
        const list = [...this.state.openedItems];
        if (this.isOpenItem(index)) {
            list.splice(list.indexOf(index), 1);
        } else {
            list.push(index);
        }

        this.setState({openedItems: list});
    }

    protected isOpenItem(index: number) {
        return this.props.openAllSubMenus !== true && this.state.openedItems.includes(index);
    }

    protected renderItems(): ReactNode {
        return this.props.items.map((item, index) => {
            if (!this.isAvailableToDisplayItem(item)) {
                return null;
            }

            if (item.section) {
                return this.renderSectionItem(item, index);
            }

            if (this.hasAvailableToDisplayItems(item.items)) {
                return this.renderSubmenuItem(item, index);
            } else if (item.items?.length) {
                return null;
            }

            return this.renderLinkItem(item, index);
        });
    }

    protected renderSectionItem(item: INavigationItem, index: number): ReactNode {
        return <Translate
                message={item.section as string}
                className={this.props.sectionItemClassName}
                tag="li"
                key={`sidebar-menu-${index}-section-item`}
        />;
    }

    protected renderLinkItem(item: INavigationItem, index: number): ReactNode {
        let Tag: string | typeof NavLink = 'a';
        let props = {};

        const uri = getUri(item) || null;
        if (uri) {
            Tag = NavLink;
            props = {to: uri, end: true};
        }

        return <li key={`sidebar-menu-${index}-item`} className={this.props.itemClassName}>
            <Tag {...props as any}>{this.renderLinkItemContent(item)}</Tag>
        </li>;
    }

    protected renderSubmenuItem(item: INavigationItem, index: number): ReactNode {
        if (!item.items) {
            return null;
        }

        const isOpen = this.isOpenItem(index);
        const SubMenu: any = this.subMenuComponent;

        return <li key={`sidebar-menu-${index}-item-with-submenu`} className={classnames({
            'has-arrow': !item.badge,
            'active': isOpen,
            [this.props.activeItemWithSubmenuClassName ?? '']: isOpen,
            [this.props.itemWithSubmenuClassName ?? '']: true
        })}>
            <a onClick={e => {
                e.stopPropagation();

                this.toggleItem(index);
            }}>
                {this.renderLinkItemContent(item)}
            </a>

            <SubMenu
                    open={isOpen}
                    items={item.items}
                    openAllSubMenus={this.props.openAllSubMenus}
                    {...(this.props.submenu ?? {})}
            />
        </li>;
    }

    protected renderLinkItemContent(item: INavigationItem): ReactNode {
        return <>
            {item.icon != null ? <i className={item.icon}/> : null}

            <Translate message={item.label ?? ''} tag="span"/>

            {item.badge != null ? (
                    <Translate
                            message={item.badge.label}
                            className={classnames({
                                [this.props.itemBadgeClassName ?? '']: true,
                                [item.badge?.class ?? '']: true,
                            })}
                            tag="span"
                    />
            ) : null}
        </>;
    }

    protected isAvailableToDisplayItem(item: P['items'][number]) {
        const context = this.context;
        if (item.permissions && (!context?.model || !context.canAny(item.permissions))) {
            return false;
        }

        return !(item.permissionsNot && (!context?.model || !context.cannotAny(item.permissionsNot)));
    }

    protected hasAvailableToDisplayItems(items: P['items'][number]['items']): boolean {
        return items?.some(item => this.isAvailableToDisplayItem(item)) === true;
    }
}
